import React from 'react'

import List from 'components_new/atoms/List'
import ListItem from 'components_new/atoms/List/ListItem'
import Text from 'components_new/atoms/Text'

import {
  ComparativePeriod,
  DateFilterType,
  ForecastPeriod,
  PeriodFilter,
  TWidgetSettings,
  WidgetType
} from 'types/GlobalWidget'

import { Option } from 'components_new/molecules/SelectSearch'

import { TFunction, TranslateEnumFunction } from 'language/translator'

export const getDateFilterOptions = (
  dateFilterOptions: PeriodFilter[],
  translateEnum: TranslateEnumFunction
): Option[] => {
  return dateFilterOptions.map((option) => ({
    title: translateEnum('period_filters', option).title,
    id: option
  }))
}

export const getDisabledComparative = (
  settings: TWidgetSettings,
  t: TFunction
) => {
  const comparativeAvailable = settings.comparative_period.available
  const hasBreakdownBy = !!settings.kpi_options.find(
    (opt) => opt.breakdown_by.selected
  )

  const disabled = !comparativeAvailable || hasBreakdownBy
  let disabledText = ''

  if (hasBreakdownBy) {
    disabledText = t('widgets', 'cannot_compare_with_breakdown')
  } else if (!comparativeAvailable) {
    disabledText = t('widgets', 'cannot_compare_for_metric_or_period')
  }

  return {
    disabledComparative: disabled,
    disabledComparativeText: disabledText
  }
}

export const getComparativePeriodOptions = (
  comparativePeriodOptions: ComparativePeriod[],
  dateFilter: PeriodFilter | null,
  dateFilterType: DateFilterType,
  t: TFunction,
  translateEnum: TranslateEnumFunction
): Option[] => {
  if (!dateFilter) {
    return []
  }

  const newOptions = comparativePeriodOptions.map((option) => {
    return {
      title: translateEnum('comparative_periods', dateFilterType)[dateFilter][
        option
      ],
      id: option
    }
  })

  return [{ title: t('global', 'none'), id: 'none' }, ...newOptions]
}

/**
 * Forecast only supports some period filters
 * and only LAST_YEAR comparison.
 */
export const DATE_FITLERS_WITH_FORECAST = [
  PeriodFilter.TODAY,
  PeriodFilter.THIS_WEEK_TO_TODAY,
  PeriodFilter.THIS_MONTH_TO_TODAY,
  PeriodFilter.THIS_QUARTER_TO_TODAY,
  PeriodFilter.THIS_YEAR_TO_TODAY,
  PeriodFilter.LAST_7_DAYS,
  PeriodFilter.LAST_30_DAYS,
  PeriodFilter.LAST_90_DAYS,
  PeriodFilter.LAST_365_DAYS,
  PeriodFilter.LAST_3_MONTHS_TO_TODAY,
  PeriodFilter.LAST_6_MONTHS_TO_TODAY,
  PeriodFilter.LAST_12_MONTHS_TO_TODAY,
  PeriodFilter.LAST_24_MONTHS_TO_TODAY,
  PeriodFilter.LAST_36_MONTHS_TO_TODAY,
  PeriodFilter.LAST_4_QUARTERS_TO_TODAY,
  PeriodFilter.LAST_8_QUARTERS_TO_TODAY,
  PeriodFilter.LAST_12_QUARTERS_TO_TODAY,
  PeriodFilter.LAST_2_YEARS_TO_TODAY,
  PeriodFilter.LAST_3_YEARS_TO_TODAY
]

export const getDisabledForecast = (
  settings: TWidgetSettings,
  t: TFunction,
  translateEnum: TranslateEnumFunction
) => {
  let disabled = false

  const disabledText = (
    <>
      {t('widgets', 'forecast_available_with_intro')}
      <List sx={{ mt: 1 }}>
        <Text color="text.secondary" component="li" variant="body2">
          {t('widgets', 'historical_periods')}
        </Text>
        {DATE_FITLERS_WITH_FORECAST.map((period) => (
          <ListItem dense={true} key={period}>
            {translateEnum('period_filters', period).title}
          </ListItem>
        ))}
        <Text
          color="text.secondary"
          component="li"
          variant="body2"
          sx={{ mt: 1 }}
        >
          {t('widgets', 'comparisons')}
        </Text>
        <ListItem dense={true}>
          {
            translateEnum('comparative_periods', DateFilterType.PERIOD)[
              settings.date_filter.selected.value
            ][ComparativePeriod.LAST_YEAR]
          }
        </ListItem>
      </List>
    </>
  )

  if (
    settings.comparative_period.selected.value &&
    [
      ComparativePeriod.AVERAGE_LAST_180_DAYS,
      ComparativePeriod.LAST_PERIOD
    ].includes(settings.comparative_period.selected.value)
  ) {
    disabled = true
  } else {
    disabled = !DATE_FITLERS_WITH_FORECAST.includes(
      settings.date_filter.selected.value
    )
  }

  return {
    disabledForecast: disabled,
    disabledForecastText: disabledText
  }
}

export const getForecastPeriodOptions = (
  t: TFunction,
  translateEnum: TranslateEnumFunction
): Option[] => {
  const newOptions = Object.values(ForecastPeriod).map((option) => {
    return {
      title: translateEnum('forecast_periods', option).title,
      id: option
    }
  })

  return [{ title: t('global', 'none'), id: 'none' }, ...newOptions]
}

export const getDisabledRestrictToPeriod = (
  settings: TWidgetSettings,
  t: TFunction,
  translateEnum: TranslateEnumFunction
) => {
  if (settings.date_filter.restrict_to_period.available) {
    return null
  }

  if (!settings.date_filter.restrict_to_period.period_type) {
    return t('widgets', 'missing_financial_period_setting')
  }

  return (
    <span>
      {t('widgets', 'cannot_restrict_period_intro')}{' '}
      <b>
        {translateEnum(
          'period_types',
          settings.date_filter.restrict_to_period.period_type
        )}
      </b>
      {t('widgets', 'adjust_interval_instruction')}
    </span>
  )
}

export const ALLOW_COMPARATIVE = [
  WidgetType.BAR_CHART,
  WidgetType.COMBO_CHART,
  WidgetType.KEY_FIGURE,
  WidgetType.LINE_CHART,
  WidgetType.MAP,
  WidgetType.LIST
]
